import { Headline, Text } from '@oneaudi/unified-web-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfigurationStep } from '~/app/compositions/sidebar/configuration-step/configuration-step';
import { DriveTrainFilter } from '~/app/compositions/sidebar/trim-line/components/drive-train-filter';
import {
	DriveTrainKeyFacts,
	useDriveTrainKeyFacts,
} from '~/app/compositions/sidebar/trim-line/components/drive-train-keyfacts';
import { IncentivesPopover } from '~/app/compositions/sidebar/trim-line/components/incentives-popover';
import {
	DriveTrainFilters,
	mapDriveToFilterValue,
	mapEngineTechnologyToFilterValue,
	useDriveTrainFilter,
} from '~/app/compositions/sidebar/trim-line/hooks/use-drivetrain-filter';
import { SelectionStepBlock } from '~/app/compositions/sidebar/trim-line/styles/selection-step-style';
import { EcoLabelElement } from '~/app/compositions/sidebar/trim-line/utils/get-eco-label';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { Headline as NGCHeadline } from '~/app/pages/configurator/components/configurator-option/components/options-content/components/headline';
import { MoreInformationTrimline } from '~/app/pages/configurator/components/more-information-dialog';
import { ModelTechnicalDataDialog } from '~/app/pages/configurator/components/technical-data-dialog';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { showTrimlineMoreInformationDialog } from '~/app/pages/configurator/models/show-more-information-dialog';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { hasTeaserText } from '~/components/selection-card-new/selection-card';
import { TrimlineSelectionCard } from '~/components/selection-card-new/trimline-selection-card';
import { useActiveCard } from '~/components/slider-panel';
import { RenderHtml } from '~/components/text';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useImages } from '~/graphql/hooks/use-images';
import { useSteps } from '~/graphql/hooks/use-steps';
import { CatalogItemType } from '~/graphql/types';
import { useBreakPoint } from '~/hooks/responsive/useBreakPoint';
import { CurrencyFormatterDisplayContext } from '~/hooks/use-currency-formatter';
import { mobileBreakPoint } from '~/styles/break-points';
import { OptionsHeader } from '../options-header';

export const TrimLine = () => {
	const { i18n } = useI18n();
	const { isLoading, isItemChecked, isItemLoading, loadingFlags } = useConfigurator();
	const { config } = useMarketConfig();
	const { selectCard: selectDriveCard } = useActiveCard();
	const { trimlineAndEngine } = useVATConfig();
	const { getKeyFacts } = useDriveTrainKeyFacts();
	const {
		catalog: { trimlines },
	} = useConfigurator();
	const { mediaByProfileId } = useImages();
	const { isCustomBreakpoint } = useBreakPoint(mobileBreakPoint);
	const { step, total } = useSteps('trimLine');
	const [activeFilters, setActiveFilters] = useState<DriveTrainFilters>({
		gearType: [],
		engineTechnology: [],
		drive: [],
	});
	const ENABLE_SHOW_DRIVE_TRAINS_COMBINED = false;

	const enginePriceDisclaimer = useMemo(
		() =>
			config.showEnginePriceDisclaimer
				? {
						id: 'engine-price-disclaimer',
						text: i18n('ngc.equipment.trimlineAndEngine.drivetrain.priceDisclaimer'),
					}
				: undefined,
		[config.showEnginePriceDisclaimer, i18n],
	);

	const sortedTrimlines = useMemo(() => {
		if (!trimlines) return [];

		return trimlines.sort((a, b) => {
			const priceA = a.totalPrice?.value || 0;
			const priceB = b.totalPrice?.value || 0;
			return priceA - priceB;
		});
	}, [trimlines]);
	const selectedTrimline = useMemo(() => {
		return sortedTrimlines.find((trimline) => isItemChecked(trimline));
	}, [isItemChecked, sortedTrimlines]);

	const driveTrains = useMemo(() => {
		return (
			selectedTrimline?.driveTrains?.sort((a, b) => {
				const priceA = a?.totalPrice?.value || 0;
				const priceB = b?.totalPrice?.value || 0;
				return priceA - priceB;
			}) ?? []
		);
	}, [selectedTrimline]);

	useEffect(() => {
		setFilteredDriveTrains(driveTrains);
	}, [JSON.stringify(driveTrains)]);

	const showCombined: boolean = useMemo(() => {
		return (
			(ENABLE_SHOW_DRIVE_TRAINS_COMBINED &&
				sortedTrimlines.length === 1 &&
				sortedTrimlines[0].driveTrains.length > 1) ||
			!sortedTrimlines.find(({ driveTrains: trains }) => trains.length > 1)
		);
	}, [ENABLE_SHOW_DRIVE_TRAINS_COMBINED, sortedTrimlines]);

	const trackSelectionClickEvent = useCallback(
		(
			eventName: string,
			label: string,
			item: CatalogItemType['trimline'] | CatalogItemType['driveTrain'],
			elementName: string,
			value: string,
			selected?: boolean,
			eventAction = EventAction.InternalLink,
		) => {
			appTrackingService.trackSelectionClickEvent({
				eventAction,
				eventName,
				label,
				elementName,
				value,
				category: item.type,
				elementCurrentStep: step,
				elementTotalSteps: total,
				selected,
				item,
			});
		},
		[step, total],
	);

	const mapToSelectionCardItem = useCallback(
		(item: CatalogItemType['trimline'] | CatalogItemType['driveTrain']) => {
			const showDetails = (detailItem: CatalogItemType['trimline'] | CatalogItemType['driveTrain']) => {
				if (detailItem.type === 'driveTrain') {
					return {
						teaserInformation:
							getKeyFacts(detailItem).length > 0 ? <DriveTrainKeyFacts drive={detailItem} /> : null,
						ecoLabel:
							detailItem.__item.ecoLabel && detailItem.__item.ecoLabel !== 'NO_ECO' ? (
								<EcoLabelElement ecoLabel={detailItem.__item.ecoLabel} />
							) : null,
						textLink: config.hasTechnicalDataEnabled ? (
							<ModelTechnicalDataDialog
								driveTrain={detailItem}
								trackSelection={() =>
									trackSelectionClickEvent(
										'click to change configuration',
										detailItem.name,
										detailItem,
										'button',
										'details layer',
										isItemChecked(detailItem),
									)
								}
								step={step}
								total={total}
							/>
						) : null,
						hidden: detailItem.hidden,
					};
				}
				return {
					teaserInformation: hasTeaserText(detailItem.__item.teaserText) ? (
						<RenderHtml html={detailItem.__item.teaserText} />
					) : null,
					ecoLabel: null,
					textLink: showTrimlineMoreInformationDialog(detailItem) ? (
						<MoreInformationTrimline
							trackSelection={() =>
								trackSelectionClickEvent(
									'click to change configuration',
									detailItem.name,
									detailItem,
									'button',
									'details layer',
									isItemChecked(detailItem),
								)
							}
							trimline={detailItem}
							step={step}
							total={total}
						/>
					) : null,
				};
			};

			return {
				name: item?.name,
				key: item.pr7,
				loading: isItemLoading(item),
				onClick: () => {
					selectDriveCard(-1, item, () =>
						trackSelectionClickEvent(
							'click to change configuration',
							item.name,
							item,
							'card',
							`selection feature app: ${item.type}: ${Object.values(activeFilters).flat().join(', ') || 'all'}`,
							isItemChecked(item),
						),
					);
				},
				readonly: loadingFlags.checkingConflicts,
				selected: isItemChecked(item),
				debugCode: item.type === 'driveTrain' ? item.debugCode : undefined,
				standard: item.status.standard,
				price: {
					trimlineAndEngine,
					monthlyRate: item.monthlyRate ?? undefined,
					totalPrice: item.totalPrice ?? undefined,
					priceDisclaimer: item.type === 'driveTrain' ? enginePriceDisclaimer : undefined,
					displayContext:
						item.type === 'trimline' && !showCombined
							? ('min-price' as CurrencyFormatterDisplayContext)
							: undefined,
				},
				incentives:
					item.type === 'driveTrain' && config.enableIncentives ? (
						<IncentivesPopover incentives={item.incentives} />
					) : null,
				...showDetails(item),
			};
		},
		[
			config.enableIncentives,
			JSON.stringify(enginePriceDisclaimer),
			JSON.stringify(trimlineAndEngine),
			trackSelectionClickEvent,
			getKeyFacts,
			isItemChecked,
			isItemLoading,
			loadingFlags.checkingConflicts,
			selectDriveCard,
			showCombined,
			activeFilters,
			step,
			total,
		],
	);

	const driveTrainFilters = useDriveTrainFilter(driveTrains);

	const [filteredDriveTrains, setFilteredDriveTrains] = useState(driveTrains);

	const filterDriveTrains = (filters: DriveTrainFilters) => {
		// if no filter is selected, show all drive trains
		const _driveTrains = structuredClone(driveTrains);
		if (!Object.values(filters).some((filter) => filter.length > 0)) {
			setFilteredDriveTrains(_driveTrains);
			return;
		}

		// filter drive trains based on selected filters
		const result = _driveTrains.filter((driveTrain) => {
			return Object.entries(filters).every(([key, filter]) => {
				// if no filter is selected, show all drive trains for the specific category
				if (filter.length === 0) return true;
				if (key === 'engineTechnology') {
					return filter.includes(
						mapEngineTechnologyToFilterValue(driveTrain?.techData?.modelAttributes?.engineTechnology),
					);
				}
				if (key === 'drive') {
					return filter.includes(mapDriveToFilterValue(driveTrain?.techData?.modelAttributes?.drive));
				}
				return filter.includes(driveTrain?.techData?.modelAttributes?.gearType);
			});
		});

		setFilteredDriveTrains(result);
	};

	const impressionTrackingRef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading && total) {
				appTrackingService.selectionStepImpression({
					node,
					value: 'trimline',
					elementCurrentStep: step,
					elementTotalSteps: total,
				});
			}
		},
		[isLoading],
	);

	const categoryDisclaimer = config.categoryDisclaimers?.find((d) => d.step === 'trimLine')?.disclaimer;

	return (
		<ConfigurationStep
			ref={impressionTrackingRef}
			hide={sortedTrimlines.length === 0}
			galleryType="onlySingleImg"
			id={'trimLine'}
			media={[
				[mediaByProfileId[ImageViewsEnum.EXTERIOR_FRONT_3_4]],
				[mediaByProfileId[ImageViewsEnum.EXTERIOR_FRONT_FRONT]],
				[mediaByProfileId[ImageViewsEnum.EXTERIOR_PARALLEL_SIDE]],
				[mediaByProfileId[ImageViewsEnum.EXTERIOR_REAR_REAR]],
				[mediaByProfileId[ImageViewsEnum.EXTERIOR_REAR_3_4]],
				[mediaByProfileId[ImageViewsEnum.EXTERIOR_WHEELS_RIM]],
				[mediaByProfileId[ImageViewsEnum.INTERIOR_DASHBOARD_DASHBOARD]],
				[mediaByProfileId[ImageViewsEnum.INTERIOR_SEATS_SEATS]],
				[mediaByProfileId[ImageViewsEnum.INTERIOR_REAR_REARSEATS]],
				[mediaByProfileId[ImageViewsEnum.EXTERIOR_REAR_TRUNK]],
			]}
			galleryControls={{ avp: true, compare: true, fullscreen: true }}
			headline={
				<div className="headline-overwrite">
					{isCustomBreakpoint && (
						<NGCHeadline hideConsumptionEmissionEngineName={config.hideConsumptionEmissionEngineName} />
					)}
					<OptionsHeader
						id={'trimLine-header'}
						title={i18n('ngc.equipment.trimlineAndEngine')}
						step={step}
						total={total}
						tabIndex={0}
					/>
				</div>
			}
			disclaimer={categoryDisclaimer}>
			{showCombined ? (
				<SelectionStepBlock tabIndex={0}>
					<div>
						{sortedTrimlines.map((trimline) => {
							const item = mapToSelectionCardItem(trimline);
							return trimline.driveTrains.map(mapToSelectionCardItem).map((driveTrain) => {
								const hidden = driveTrain.hidden ? ' [HIDDEN-ENGINE]' : '';
								const name = `${driveTrain.name}${hidden}`;

								return (
									<TrimlineSelectionCard
										id={item.key}
										item={{
											...item,
											debugCode: `${item.key} | ${driveTrain.debugCode}`,
											incentives: driveTrain.incentives,
											name,
											onDescriptionToggle: (showDescription: boolean) =>
												trackSelectionClickEvent(
													`click to ${showDescription ? 'close' : 'open'} details accordion`,
													showDescription
														? i18n('ngc.actions.hideDetails')
														: i18n('ngc.actions.showDetails'),
													trimline,
													'text link',
													'details accordion',
													undefined,
													EventAction.Content,
												),
											year: trimline.driveTrains[0].modelId.year.toString(),
											prCode: `${item.key} | ${driveTrain.key}`,
										}}
										key={driveTrain.key}
										textLinks={
											<>
												{item.textLink}
												{driveTrain.textLink}
											</>
										}>
										{item.teaserInformation}
										{driveTrain.ecoLabel}
										{driveTrain.teaserInformation}
									</TrimlineSelectionCard>
								);
							});
						})}
					</div>
				</SelectionStepBlock>
			) : (
				<>
					<SelectionStepBlock tabIndex={0} aria-labelledby="trimline-headline">
						{isCustomBreakpoint && (
							<Headline
								id="trimline-headline"
								variant={{ order: '4', type: 'headline', weight: 'normal' }}
								tag="h3">
								{i18n('ngc.equipment.trimline')}
							</Headline>
						)}
						<div>
							{sortedTrimlines.map((trimline) => {
								const item = mapToSelectionCardItem(trimline);
								return (
									<TrimlineSelectionCard
										id={item.key}
										item={{
											...item,
											onDescriptionToggle: (showDescription: boolean) =>
												trackSelectionClickEvent(
													`click to ${showDescription ? 'close' : 'open'} details accordion`,
													showDescription
														? i18n('ngc.actions.hideDetails')
														: i18n('ngc.actions.showDetails'),
													trimline,
													'text link',
													'details accordion',
													undefined,
													EventAction.Content,
												),
											prCode: trimline.pr7,
											year: trimline.model?.year.toString(),
										}}
										key={trimline.pr7}
										textLinks={item.textLink}>
										{item.teaserInformation}
									</TrimlineSelectionCard>
								);
							})}
						</div>
					</SelectionStepBlock>
					<SelectionStepBlock tabIndex={0} aria-labelledby={'engine-headline'} style={{ marginTop: '20px' }}>
						<Headline
							id="engine-headline"
							variant={{ order: '4', type: 'headline', weight: 'normal' }}
							tag="h3">
							{i18n('ngc.equipment.engine')}
						</Headline>
						<DriveTrainFilter
							activeFilters={activeFilters}
							setActiveFilters={setActiveFilters}
							availableFilters={driveTrainFilters}
							filterDriveTrains={filterDriveTrains}
							driveTrains={filteredDriveTrains}
							selectedTrimline={sortedTrimlines.find((d) => d.status.selected)?.pr7}
							step={step}
							total={total}
						/>
						<div>
							{filteredDriveTrains.length ? (
								filteredDriveTrains.map((driveTrain) => {
									const item = mapToSelectionCardItem(driveTrain);
									const name = !item.hidden ? item.name : `${item.name} [HIDDEN-ENGINE]`;

									return (
										<TrimlineSelectionCard
											id={item.key}
											item={{
												...item,
												name,
												onDescriptionToggle: (showDescription: boolean) =>
													trackSelectionClickEvent(
														`click to ${showDescription ? 'close' : 'open'} details accordion`,
														showDescription
															? i18n('ngc.actions.hideDetails')
															: i18n('ngc.actions.showDetails'),
														driveTrain,
														'text link',
														'details accordion',
														undefined,
														EventAction.Content,
													),
												prCode: driveTrain.pr7,
												year: driveTrain.modelId.year.toString(),
											}}
											key={item.key}
											textLinks={item.textLink}>
											{item.ecoLabel}
											{item.teaserInformation}
										</TrimlineSelectionCard>
									);
								})
							) : (
								<Text
									tag="p"
									variant={{ type: 'copy', order: '1', style: 'normal' }}
									className={'error'}>
									{i18n('ngc.engineFilter.noResults')}
								</Text>
							)}
						</div>
					</SelectionStepBlock>
				</>
			)}
		</ConfigurationStep>
	);
};
